import React, { useState, useMemo } from "react";
import SEO from "../components/seo";
import { Text, Flex, Heading } from "rebass";
import Parallax from "../components/parallax";
import { graphql } from "gatsby";
import BackgroundSlide from "../components/backgroundSlide";
import Blur from "../components/blur";
import BlurredText from "../components/blurredText";
import GatsbyImage from "gatsby-image";
import { useColorMode } from "theme-ui";
import { pathOr, isEmpty } from "ramda";
import Fullpage from "../components/fullpage";
import FirstSlide from "../components/firstSlide";
import { useMediaQuery } from "react-responsive";
import theme from "../gatsby-plugin-theme-ui";
import { getFieldsByName, useGetFieldsByName } from "../utils";
import useFirstPose from "../hooks/useFirstPose";
import { ModuleCarousel } from '../components/paragpraphs';

const ExpertisePage = ({ data }) => {
  const {
    parallax, parallaxMobile, expertise, icons: iconsArray, ogImage } = data;
  const {
    textSeq,
    videosSeq,
    groupsSeq,
    blocks: { blockGroup, blockVideos },
  } = expertise;

  const isTabletOrMobile = useMediaQuery({ maxWidth: theme.breakpoints[1] });

  const icons = useMemo(
    () => iconsArray.nodes.reduce((res, item) => ({ ...res, [item.name]: item }), {}),
    [iconsArray]
  );

  const [colorMode] = useColorMode();
  const [activeBlock, setActiveBlock] = useState([0, 1]);

  const leftColumn = expertise[`leftColumn_${colorMode}`];
  const rightColumn = expertise[`rightColumn_${colorMode}`];
  const titleBottom = expertise[`titleBottom_${colorMode}`];
  const { title, subtitle, description } = useGetFieldsByName(['title', 'subtitle', 'description'], expertise, colorMode);

  const pose = useFirstPose();

  const sectionsFunc = {
    textSection:  () => <Flex flexDirection="column" alignItems="center" maxWidth={[null, 700, 900, 1100]} mb={[5, 6, 0]}>
      {titleBottom && <Heading as="h3" fontSize={[6, null, null, 7]} width={['100%']} px={[0, null, 0]} mb={[3, 4, 5]} sx={{ textAlign: ['left', 'center'] }}>{titleBottom}</Heading>}
      <Flex justifyContent="center" flexDirection={['column', 'row']} alignItems="flex-start" px={[0, null, 0]} maxWidth={[320, 700, 'auto']}>
        <Text sx={{ whiteSpace: 'pre-wrap', flexBasis: ['100%', '50%'], flexShrink: 0, }} mb={['1em', 0]} pr={[null, 4]}>{leftColumn}</Text>
        <Text sx={{ whiteSpace: 'pre-wrap', flexBasis: ['100%', '50%'], flexShrink: 0, }} pl={[null, 4]}>{rightColumn}</Text>
      </Flex>
    </Flex>,
    videoSection: ({ active }) => <ModuleCarousel module={blockVideos[0]} active={active} bg="none" />,
    groupSection: () => <Flex flexWrap={[null, null, "wrap"]} flexDirection={['column', null, 'row']} justifyContent="center" maxWidth={1200} mb={[5, 6, 0]}>
      {blockGroup.map((block, order) => {
        const icon = pathOr({}, [`icon-${order % iconsArray.nodes.length}`, 'childImageSharp', 'fixed'], icons);
        const { title, description } = getFieldsByName(['title', 'description'], block, colorMode);
        return (
          <BlurredText
            active={activeBlock[0] === order}
            past={activeBlock[1] === order}
            key={block.id}
            flexDirection="column"
            alignItems={[order % 2 ? 'flex-end' : 'flex-start', null, "center"]}
            mb={[4, null, 5]}
            onMouseEnter={() => setActiveBlock(
              ([lastActive]) => [order, lastActive !== order ? lastActive : (lastActive + 1) % blockGroup.length]
            )}
            sx={{ willChange: 'filter', flexBasis: '50%', order }}
          >
            <Heading as="h2" fontSize={[6, null, 7, 9]}>{title}</Heading>
            <Flex
              mt={[3]}
              flexDirection={['column-reverse', null, 'row']}
              alignItems={[order % 2 ? 'flex-end' : 'flex-start', null, "flex-start"]}
              sx={{
                textAlign: [order % 2 ? 'right' : 'left', null, 'left'],
                'div:first-of-type': { marginTop: [3], filter: colorMode === 'default' ? 'invert(1)' : 'invert(0)' }
              }}
            >
              {!isEmpty(icon) ? <GatsbyImage fixed={icon} /> : null}
              <Text
                fontSize={1}
                maxWidth={[320, null, 'auto']}
                color={"text"}
                ml={[null, null, 4]}
              >
                {description}
              </Text>
            </Flex>
          </BlurredText>
        ) })}
    </Flex>,
  };
  const sectionsPositions = {
    textSection: textSeq,
    groupSection: groupsSeq,
    videoSection: videosSeq,
  };
  const sections = (textSeq && groupsSeq && videosSeq)
    ? Object.keys(sectionsPositions)
      .sort((a, b) => sectionsPositions[a] - sectionsPositions[b])
      .map((key) => key)
    : Object.keys(sectionsFunc);

  return (
    <>
      <SEO title="Agence" image={ogImage}/>
      <Fullpage
        background={({ blurred }) => (
          <Blur active={blurred} darken={colorMode === 'dark'} lighten={colorMode === 'default'} sx={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0, willChange: 'filter', overflow: 'hidden' }}>
            <BackgroundSlide
              mixedBg={<Parallax layers={isTabletOrMobile ? parallaxMobile.layers : parallax.layers} disabled={blurred || pose === 'hidden'} />}
            />
          </Blur>
        )}
        sections={[
          () => <FirstSlide color="black" title={title} subtitle={subtitle} description={description} pose={pose} />,
          ...sections.map((section) => sectionsFunc[section]),
        ]}
      />
    </>
  )
}

export default ExpertisePage;

export const query = graphql`
  query {
    expertise: nodeExpertise {
      subtitle: field_sub_title
      title
      description: field_description_brut
      title_dark: field_dark_titre
      subtitle_dark: field_dark_sub_title
      description_dark: field_dark_description_brut
      id
      ctaButtonText: field_expertise_cta_button
      leftColumn_dark: field_dark_left_column
      rightColumn_dark: field_dark_right_column
      titleBottom_dark: field_dark_title
      leftColumn_default: field_left_column
      rightColumn_default: field_right_column
      titleBottom_default: field_title
      textSeq: field_texts_sequence_number
      videosSeq: field_videos_sequence_number
      groupsSeq: field_groups_sequence_number
      blocks: relationships {
        blockGroup: field_expertise_group {
          title: field_title
          description: field_description_brut
          title_dark: field_dark_titre
          description_dark: field_dark_description_brut
          id
        }
        blockVideos: field_module {
          ... on paragraph__module_caroussel {
            ...moduleCarouselFragment
          }
        }
      }
    }
    parallax: configsJson(name: {eq: "expertise-mixed"}) {
      layers {
        depth
        overflow
        offsetY
        animation {
          pose {
            opacity
            x
            y
          }
          keyframes {
            duration
            values
            times
          }
          styles {
            opacity
          }
        }
        image {
          id
          childImageSharp {
            original {
              width
              height
            }
            fluid(maxWidth: 3840, quality: 80) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    }
    parallaxMobile: configsJson(name: {eq: "expertise-mobile"}) {
      layers {
        depth
        overflow
        image {
          id
          childImageSharp {
            original {
              width
              height
            }
            fluid(maxWidth: 1280, quality: 50) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    }
    icons: allFile(filter: {relativeDirectory: {regex: "/^expertise/"}}) {
      nodes {
        childImageSharp {
          fixed(quality: 50, width: 90) {
            ...GatsbyImageSharpFixed_withWebp
          }
        }
        id
        name
        relativePath
      }
    }
    ogImage: file(name: {eq: "og_expertise"}) {
      ...ogImageFragment
    }
  }
`
